import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Loader2, FileText } from "lucide-react";

const TABS = ["overview", "episodes", "articles", "members", "partners", "funding", "credit-readiness", "newsletter", "campaigns", "leads", "licenses", "franchise", "payments", "contact", "affiliate"];

const blankEpisode = { title: "", guest_name: "", guest_title: "", host: "Wealth First Editorial", synopsis: "", cover_image: "", video_url: "", runtime: "00:00", industry: "", tier: "gold", featured: false };
const blankArticle = { title: "", subtitle: "", author: "", cover_image: "", excerpt: "", body: "", category: "", tier: "free" };
const blankMember = { name: "", title: "", company: "", industry: "", tier: "UHNW", portrait: "", bio: "", location: "", featured: false };

const EPISODE_LIST_FIELDS = ["title", "guest_name", "industry", "tier"];
const ARTICLE_LIST_FIELDS = ["title", "author", "category", "tier"];
const MEMBER_LIST_FIELDS = ["name", "company", "industry", "tier"];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [articles, setArticles] = useState([]);
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null); // { type, item }
  const [partners, setPartners] = useState([]);
  const [funding, setFunding] = useState([]);
  const [contact, setContact] = useState([]);
  const [affiliate, setAffiliate] = useState([]);
  const [franchise, setFranchise] = useState([]);
  const [payments, setPayments] = useState([]);
  const [creditReadiness, setCreditReadiness] = useState([]);
  const [newsletterData, setNewsletterData] = useState({ issues: [], subscribers: [], stats: {} });
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsByCampaign, setLeadsByCampaign] = useState({ rows: [], totals: {} });
  const [licenses, setLicenses] = useState({ licensees: [], totals: {} });

  const loadAll = useCallback(async () => {
    const [s, e, a, m, p, f] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/episodes"),
      api.get("/articles"),
      api.get("/members"),
      api.get("/admin/partners"),
      api.get("/admin/funding"),
    ]);
    setStats(s.data); setEpisodes(e.data); setArticles(a.data); setMembers(m.data);
    setPartners(p.data); setFunding(f.data);
    try {
      const [c, af, fr, pay, cr, nIssues, nSubs, camps, lds, lbc, lic] = await Promise.all([
        api.get("/admin/contact"),
        api.get("/admin/affiliate"),
        api.get("/admin/franchise"),
        api.get("/admin/payments"),
        api.get("/admin/credit-readiness"),
        api.get("/admin/newsletter/issues"),
        api.get("/admin/newsletter/subscribers"),
        api.get("/admin/campaigns"),
        api.get("/admin/leads"),
        api.get("/admin/leads/by-campaign"),
        api.get("/admin/licenses"),
      ]);
      setContact(c.data); setAffiliate(af.data); setFranchise(fr.data); setPayments(pay.data); setCreditReadiness(cr.data);
      setNewsletterData({
        issues: nIssues.data || [],
        subscribers: nSubs.data?.subscribers || [],
        stats: nSubs.data?.stats || {},
      });
      setCampaigns(camps.data?.campaigns || []);
      setLeads(lds.data?.leads || []);
      setLeadsByCampaign({ rows: lbc.data?.rows || [], totals: lbc.data?.totals || {} });
      setLicenses({ licensees: lic.data?.licensees || [], totals: lic.data?.totals || {} });
    } catch (err) {
      console.warn("Optional admin endpoints unavailable:", err?.response?.status);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") {
      loadAll().catch((error) => console.error("Admin load failed:", error));
    }
  }, [user, loadAll]);

  if (user === null) return <div className="pt-40 text-center" style={{ color: "var(--ink-muted)" }}>Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login?next=/admin" replace />;

  const handleDelete = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${type}/${id}`);
      toast.success("Deleted.");
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  const openCreate = (type) => {
    const blank = type === "episodes" ? blankEpisode : type === "articles" ? blankArticle : blankMember;
    setEditing({ type, item: { ...blank }, isNew: true });
  };
  const openEdit = (type, item) => setEditing({ type, item: { ...item }, isNew: false });

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-3">Editorial Desk</p>
          <h1 className="font-display text-5xl md:text-6xl">Administration</h1>
        </div>
        <p className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ink-faint)" }}>
          Signed in as {user.email}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-10 flex-wrap" style={{ borderBottom: "1px solid var(--hairline)" }}>
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className="text-xs uppercase tracking-[0.22em] px-4 py-3 -mb-px transition-colors"
            style={{
              color: tab === t ? "var(--brass)" : "var(--ink-muted)",
              borderBottom: tab === t ? "1px solid var(--brass)" : "1px solid transparent",
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="admin-overview">
          {Object.entries(stats).map(([k, v]) => (
            <div key={k} className="p-6" style={{ border: "1px solid var(--hairline)" }}>
              <p className="eyebrow mb-3">{k.replace(/_/g, " ")}</p>
              <p className="font-display text-4xl" style={{ color: "var(--brass)" }}>{v}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "episodes" && (
        <AdminList type="episodes" items={episodes} onAdd={() => openCreate("episodes")} onEdit={(i) => openEdit("episodes", i)} onDelete={(id) => handleDelete("episodes", id)} fields={EPISODE_LIST_FIELDS} />
      )}
      {tab === "articles" && (
        <AdminList type="articles" items={articles} onAdd={() => openCreate("articles")} onEdit={(i) => openEdit("articles", i)} onDelete={(id) => handleDelete("articles", id)} fields={ARTICLE_LIST_FIELDS} />
      )}
      {tab === "members" && (
        <AdminList type="members" items={members} onAdd={() => openCreate("members")} onEdit={(i) => openEdit("members", i)} onDelete={(id) => handleDelete("members", id)} fields={MEMBER_LIST_FIELDS} />
      )}
      {tab === "partners" && (
        <PartnersAdmin items={partners} reload={loadAll} />
      )}
      {tab === "funding" && (
        <FundingAdmin items={funding} reload={loadAll} />
      )}
      {tab === "franchise" && (
        <FranchiseAdmin items={franchise} reload={loadAll} />
      )}
      {tab === "payments" && (
        <PaymentsAdmin items={payments} />
      )}
      {tab === "credit-readiness" && (
        <CreditReadinessAdmin items={creditReadiness} />
      )}
      {tab === "newsletter" && (
        <NewsletterAdmin
          issues={newsletterData.issues}
          subscribers={newsletterData.subscribers}
          stats={newsletterData.stats}
          reload={loadAll}
        />
      )}
      {tab === "contact" && (
        <ContactAdmin items={contact} reload={loadAll} />
      )}
      {tab === "affiliate" && (
        <AffiliateAdmin items={affiliate} reload={loadAll} />
      )}
      {tab === "campaigns" && (
        <CampaignsAdmin items={campaigns} reload={loadAll} />
      )}
      {tab === "leads" && (
        <LeadsAdmin leads={leads} byCampaign={leadsByCampaign} reload={loadAll} />
      )}
      {tab === "licenses" && (
        <LicensesAdmin data={licenses} reload={loadAll} />
      )}

      {editing && (
        <EditDrawer editing={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await loadAll(); }} />
      )}
    </div>
  );
}

const PARTNER_STATUSES = ["new", "reviewing", "accepted", "declined", "archived"];
const KIND_LABEL = {
  sponsor: "Sponsorship",
  press: "Press / Media",
  guest_nomination: "Guest Nomination",
  brand_partnership: "Brand Partnership",
  general: "General",
};

function PartnersAdmin({ items, reload }) {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/partners/${id}`, { status });
      toast.success("Status updated.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/admin/partners/${id}`);
      toast.success("Deleted.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div data-testid="admin-partners-list">
      <div className="flex gap-2 mb-6 flex-wrap">
        {["all", ...PARTNER_STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`partners-filter-${s}`}
            className="text-xs uppercase tracking-[0.18em] px-3 py-1.5 transition-colors"
            style={{
              border: "1px solid",
              borderColor: filter === s ? "var(--brass)" : "var(--hairline)",
              color: filter === s ? "var(--brass)" : "var(--ink-muted)",
            }}
          >
            {s} {s === "all" ? `(${items.length})` : `(${items.filter((i) => i.status === s).length})`}
          </button>
        ))}
      </div>
      <div style={{ border: "1px solid var(--hairline)" }}>
        {filtered.length === 0 && (
          <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No inquiries in this view.</p>
        )}
        {filtered.map((p, i) => (
          <div key={p.id} style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
            <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center cursor-pointer hover:bg-[var(--surface)] transition-colors" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
              <div className="md:col-span-3">
                <p className="eyebrow mb-1" style={{ color: p.status === "new" ? "var(--brass)" : "var(--ink-faint)" }}>{p.status}</p>
                <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>{KIND_LABEL[p.kind] || p.kind}</p>
              </div>
              <div className="md:col-span-4">
                <p className="font-display text-xl">{p.name}</p>
                <p className="text-xs" style={{ color: "var(--ink-muted)" }}>{p.email}</p>
              </div>
              <div className="md:col-span-3 text-sm" style={{ color: "var(--ink-muted)" }}>
                {p.organization || "—"}
              </div>
              <div className="md:col-span-2 text-xs text-right" style={{ color: "var(--ink-faint)" }}>
                {new Date(p.submitted_at).toLocaleDateString()}
              </div>
            </div>
            {expanded === p.id && (
              <div className="px-5 pb-5 pt-2" style={{ background: "var(--surface)", borderTop: "1px solid var(--hairline)" }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5 text-sm">
                  <div><p className="eyebrow mb-1">Role</p><p>{p.role || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Website</p><p>{p.website ? <a href={p.website} target="_blank" rel="noreferrer" className="brass-link">{p.website}</a> : "—"}</p></div>
                </div>
                <p className="eyebrow mb-2">Message</p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap mb-6" style={{ color: "var(--ink)" }}>{p.message}</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="eyebrow">Set Status:</span>
                  {PARTNER_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(p.id, s)}
                      disabled={p.status === s}
                      data-testid={`set-status-${p.id}-${s}`}
                      className="text-xs uppercase tracking-[0.16em] px-3 py-1.5 transition-colors"
                      style={{
                        border: "1px solid",
                        borderColor: p.status === s ? "var(--brass)" : "var(--hairline-strong)",
                        color: p.status === s ? "var(--brass)" : "var(--ink)",
                        opacity: p.status === s ? 0.6 : 1,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove(p.id)} className="ml-auto text-xs uppercase tracking-[0.16em] px-3 py-1.5" style={{ color: "#E08585", border: "1px solid rgba(224,133,133,0.4)" }} data-testid={`delete-partner-${p.id}`}>
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminList({ type, items, onAdd, onEdit, onDelete, fields }) {
  return (
    <div data-testid={`admin-${type}-list`}>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm uppercase tracking-[0.2em]" style={{ color: "var(--ink-muted)" }}>{items.length} items</p>
        <button onClick={onAdd} className="btn-brass !py-2 !px-4" data-testid={`admin-add-${type}`}>
          <Plus size={14} /> Add New
        </button>
      </div>
      <div style={{ border: "1px solid var(--hairline)" }}>
        <table className="w-full">
          <thead>
            <tr style={{ borderBottom: "1px solid var(--hairline)" }}>
              {fields.map((f) => (
                <th key={f} className="text-left p-4 eyebrow">{f.replace(/_/g, " ")}</th>
              ))}
              <th className="text-right p-4 eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.id} style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
                {fields.map((f) => (
                  <td key={f} className="p-4 text-sm" style={{ color: "var(--ink)" }}>{String(item[f] ?? "—")}</td>
                ))}
                <td className="p-4 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => onEdit(item)} className="p-2 hover:text-[var(--brass)] transition-colors" data-testid={`edit-${item.id}`}><Pencil size={14} /></button>
                    <button onClick={() => onDelete(item.id)} className="p-2 hover:text-[var(--brass)] transition-colors" data-testid={`delete-${item.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={fields.length + 1} className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No items.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditDrawer({ editing, onClose, onSaved }) {
  const { type, item, isNew } = editing;
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.published_at;
      delete payload.created_at;
      if (isNew) {
        await api.post(`/admin/${type}`, payload);
      } else {
        await api.put(`/admin/${type}/${item.id}`, payload);
      }
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save.");
    } finally {
      setBusy(false);
    }
  };

  // Hide system fields
  const fields = Object.keys(form).filter((k) => !["id", "published_at", "created_at"].includes(k));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(9,10,12,0.8)" }}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" style={{ background: "var(--surface)", border: "1px solid var(--hairline-strong)" }} data-testid="edit-drawer">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-3xl">{isNew ? "New" : "Edit"} {type.slice(0, -1)}</h2>
          <button onClick={onClose} data-testid="edit-close"><X size={20} /></button>
        </div>
        <div className="space-y-5">
          {fields.map((k) => (
            <div key={k}>
              <label className="eyebrow block mb-2">{k.replace(/_/g, " ")}</label>
              {typeof form[k] === "boolean" ? (
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <input type="checkbox" checked={form[k]} onChange={(e) => update(k, e.target.checked)} />
                  <span>{form[k] ? "Yes" : "No"}</span>
                </label>
              ) : k === "body" || k === "synopsis" || k === "bio" || k === "excerpt" ? (
                <textarea
                  value={form[k] || ""}
                  onChange={(e) => update(k, e.target.value)}
                  rows={k === "body" ? 8 : 4}
                  className="w-full bg-transparent p-3 text-sm outline-none"
                  style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }}
                />
              ) : (
                <input
                  type="text"
                  value={form[k] || ""}
                  onChange={(e) => update(k, e.target.value)}
                  className="input-line"
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-brass" data-testid="edit-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}


const FUNDING_STATUSES = ["new", "reviewing", "approved", "declined", "archived"];
const PLAN_LABEL = {
  ai_report: "AI Report",
  pro_monthly: "PRO Monthly",
  pro_annual: "PRO Annual",
  autopilot: "AutoPilot",
};

function FundingAdmin({ items, reload }) {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [generating, setGenerating] = useState({}); // { [appId]: true }
  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  // Poll any items currently generating
  useEffect(() => {
    const generatingIds = items.filter((i) => i.report_status === "generating").map((i) => i.id);
    if (generatingIds.length === 0) return;
    const interval = setInterval(() => { reload(); }, 8000);
    return () => clearInterval(interval);
  }, [items, reload]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/funding/${id}`, { status });
      toast.success("Status updated.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`/admin/funding/${id}`);
      toast.success("Deleted.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  const generate = async (id) => {
    setGenerating((g) => ({ ...g, [id]: true }));
    try {
      await api.post(`/admin/funding/${id}/generate-report`);
      toast.success("AI report generation started. This typically takes 3-5 minutes.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start generation.");
    } finally {
      setGenerating((g) => ({ ...g, [id]: false }));
    }
  };

  return (
    <div data-testid="admin-funding-list">
      <div className="flex gap-2 mb-6 flex-wrap">
        {["all", ...FUNDING_STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`funding-filter-${s}`}
            className="text-xs uppercase tracking-[0.18em] px-3 py-1.5 transition-colors"
            style={{
              border: "1px solid",
              borderColor: filter === s ? "var(--brass)" : "var(--hairline)",
              color: filter === s ? "var(--brass)" : "var(--ink-muted)",
            }}
          >
            {s} {s === "all" ? `(${items.length})` : `(${items.filter((i) => i.status === s).length})`}
          </button>
        ))}
      </div>
      <div style={{ border: "1px solid var(--hairline)" }}>
        {filtered.length === 0 && (
          <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No applications in this view.</p>
        )}
        {filtered.map((p, i) => (
          <div key={p.id} style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
            <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center cursor-pointer hover:bg-[var(--surface)] transition-colors" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
              <div className="md:col-span-3">
                <p className="eyebrow mb-1" style={{ color: p.status === "new" ? "var(--brass)" : "var(--ink-faint)" }}>{p.status}</p>
                <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>{PLAN_LABEL[p.plan] || p.plan}</p>
              </div>
              <div className="md:col-span-3">
                <p className="font-display text-xl">{p.name}</p>
                <p className="text-xs" style={{ color: "var(--ink-muted)" }}>{p.email}</p>
              </div>
              <div className="md:col-span-3 text-sm" style={{ color: "var(--ink-muted)" }}>
                {p.business_name || "—"}
              </div>
              <div className="md:col-span-3 text-right">
                {p.report_status === "ready" && (
                  <span className="text-xs uppercase tracking-[0.18em] px-2 py-1" style={{ color: "var(--brass)", border: "1px solid var(--brass)" }}>★ Report Ready</span>
                )}
                {p.report_status === "generating" && (
                  <span className="text-xs uppercase tracking-[0.18em] px-2 py-1 inline-flex items-center gap-2" style={{ color: "var(--brass)" }}>
                    <Loader2 size={12} className="animate-spin" /> Generating
                  </span>
                )}
                {p.report_status === "failed" && (
                  <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "#E08585" }}>Failed</span>
                )}
                <p className="text-xs mt-1" style={{ color: "var(--ink-faint)" }}>
                  {new Date(p.submitted_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            {expanded === p.id && (
              <div className="px-5 pb-5 pt-2 text-sm" style={{ background: "var(--surface)", borderTop: "1px solid var(--hairline)" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div><p className="eyebrow mb-1">Phone</p><p>{p.phone || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Business Type</p><p>{p.business_type || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Annual Revenue</p><p>{p.annual_revenue || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Funding Goal</p><p>{p.funding_goal || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Timeline</p><p>{p.timeline || "—"}</p></div>
                </div>
                {p.notes && (<><p className="eyebrow mb-2">Notes</p><p className="whitespace-pre-wrap mb-6" style={{ color: "var(--ink)" }}>{p.notes}</p></>)}

                {/* AI Report Section */}
                <div className="my-6 p-5" style={{ border: "1px solid var(--brass)", background: "var(--bg)" }} data-testid={`report-section-${p.id}`}>
                  <p className="eyebrow mb-3" style={{ color: "var(--brass)" }}>✦ AI Funding Compliance Report</p>
                  {p.report_status === "ready" ? (
                    <div className="flex flex-wrap gap-3 items-center">
                      <p className="text-sm flex-1" style={{ color: "var(--ink)" }}>
                        Report generated {p.report_generated_at && new Date(p.report_generated_at).toLocaleString()}
                      </p>
                      <a
                        href={`/funding/report?id=${p.id}&email=${encodeURIComponent(p.email)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-brass !py-2 !px-4"
                        data-testid={`view-report-${p.id}`}
                      >
                        <FileText size={14} /> View Report
                      </a>
                      <button
                        onClick={() => generate(p.id)}
                        disabled={generating[p.id]}
                        className="btn-ghost !py-2 !px-4"
                        data-testid={`regenerate-${p.id}`}
                      >
                        {generating[p.id] ? "Starting…" : "Regenerate"}
                      </button>
                    </div>
                  ) : p.report_status === "generating" ? (
                    <div className="flex items-center gap-3">
                      <Loader2 size={16} className="animate-spin" style={{ color: "var(--brass)" }} />
                      <p className="text-sm" style={{ color: "var(--ink-muted)" }}>
                        AI is analyzing the profile against all nine funding types. Typically 3-5 minutes.
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-3 items-center justify-between">
                      <p className="text-sm" style={{ color: "var(--ink-muted)" }}>
                        {p.report_status === "failed"
                          ? `Generation failed: ${p.report_error || "Unknown error"}`
                          : "No report yet. Generate a personalized compliance & regulatory readiness analysis."}
                      </p>
                      <button
                        onClick={() => generate(p.id)}
                        disabled={generating[p.id]}
                        className="btn-brass !py-2 !px-4"
                        data-testid={`generate-${p.id}`}
                      >
                        {generating[p.id] ? "Starting…" : "Generate AI Report"}
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <span className="eyebrow">Status:</span>
                  {FUNDING_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(p.id, s)}
                      disabled={p.status === s}
                      data-testid={`funding-status-${p.id}-${s}`}
                      className="text-xs uppercase tracking-[0.16em] px-3 py-1.5 transition-colors"
                      style={{
                        border: "1px solid",
                        borderColor: p.status === s ? "var(--brass)" : "var(--hairline-strong)",
                        color: p.status === s ? "var(--brass)" : "var(--ink)",
                        opacity: p.status === s ? 0.6 : 1,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove(p.id)} className="ml-auto text-xs uppercase tracking-[0.16em] px-3 py-1.5" style={{ color: "#E08585", border: "1px solid rgba(224,133,133,0.4)" }} data-testid={`delete-funding-${p.id}`}>
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactAdmin({ items, reload }) {
  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/admin/contact/${id}`);
      toast.success("Deleted.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div data-testid="admin-contact-list">
      <p className="text-sm uppercase tracking-[0.2em] mb-6" style={{ color: "var(--ink-muted)" }}>{items.length} messages</p>
      <div style={{ border: "1px solid var(--hairline)" }}>
        {items.length === 0 && (
          <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No messages yet.</p>
        )}
        {items.map((m, i) => (
          <div key={m.id} className="p-5" style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
            <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
              <div>
                <p className="font-display text-xl mb-1">{m.subject || "(No subject)"}</p>
                <p className="text-xs" style={{ color: "var(--ink-muted)" }}>
                  From <span style={{ color: "var(--brass)" }}>{m.name}</span> &lt;{m.email}&gt; · {new Date(m.submitted_at).toLocaleDateString()}
                </p>
              </div>
              <button onClick={() => remove(m.id)} className="text-xs uppercase tracking-[0.16em] px-3 py-1.5" style={{ color: "#E08585", border: "1px solid rgba(224,133,133,0.4)" }} data-testid={`delete-contact-${m.id}`}>
                Delete
              </button>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ink)" }}>{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AffiliateAdmin({ items, reload }) {
  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`/admin/affiliate/${id}`);
      toast.success("Deleted.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div data-testid="admin-affiliate-list">
      <p className="text-sm uppercase tracking-[0.2em] mb-6" style={{ color: "var(--ink-muted)" }}>{items.length} applications</p>
      <div style={{ border: "1px solid var(--hairline)" }}>
        {items.length === 0 && (
          <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No applications yet.</p>
        )}
        {items.map((a, i) => (
          <div key={a.id} className="p-5" style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
            <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
              <div>
                <p className="font-display text-xl mb-1">{a.name}</p>
                <p className="text-xs" style={{ color: "var(--ink-muted)" }}>{a.email} · {new Date(a.submitted_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => remove(a.id)} className="text-xs uppercase tracking-[0.16em] px-3 py-1.5" style={{ color: "#E08585", border: "1px solid rgba(224,133,133,0.4)" }} data-testid={`delete-affiliate-${a.id}`}>
                Delete
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
              <div><p className="eyebrow mb-1">Audience</p><p style={{ color: "var(--ink)" }}>{a.audience}</p></div>
              <div><p className="eyebrow mb-1">Channels</p><p style={{ color: "var(--ink)" }}>{a.channels}</p></div>
              {a.tracks?.length > 0 && (
                <div className="md:col-span-2">
                  <p className="eyebrow mb-2">Commission Tracks</p>
                  <div className="flex flex-wrap gap-2">
                    {a.tracks.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-[0.18em] px-2 py-1"
                        style={{
                          border: "1px solid var(--brass)",
                          color: t === "brand_license" ? "var(--brass)" : "var(--ink-muted)",
                          background: t === "brand_license" ? "rgba(194, 163, 115, 0.08)" : "transparent",
                        }}
                      >
                        {t.replace("_", " ")}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {a.promo_idea && (
                <div className="md:col-span-2"><p className="eyebrow mb-1">Promo Idea</p><p style={{ color: "var(--ink)" }}>{a.promo_idea}</p></div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const FRANCHISE_STATUSES = ["new", "reviewing", "approved", "active", "declined", "archived"];
const FRANCHISE_TIER_LABEL = {
  starter: "Starter",
  grow_rich: "Grow-Rich",
  enterprise: "Enterprise",
};
const PAYMENT_OPTION_LABEL = {
  upfront: "Upfront",
  installments: "4-pay plan",
  "n/a": "Standard",
};

function FranchiseAdmin({ items, reload }) {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/franchise/${id}`, { status });
      toast.success("Status updated.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this franchise application?")) return;
    try {
      await api.delete(`/admin/franchise/${id}`);
      toast.success("Deleted.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div data-testid="admin-franchise-list">
      <div className="flex gap-2 mb-6 flex-wrap">
        {["all", ...FRANCHISE_STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`franchise-filter-${s}`}
            className="text-xs uppercase tracking-[0.18em] px-3 py-1.5 transition-colors"
            style={{
              border: "1px solid",
              borderColor: filter === s ? "var(--brass)" : "var(--hairline)",
              color: filter === s ? "var(--brass)" : "var(--ink-muted)",
            }}
          >
            {s} {s === "all" ? `(${items.length})` : `(${items.filter((i) => i.status === s).length})`}
          </button>
        ))}
      </div>
      <div style={{ border: "1px solid var(--hairline)" }}>
        {filtered.length === 0 && (
          <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No franchise applications in this view.</p>
        )}
        {filtered.map((p, i) => (
          <div key={p.id} style={{ borderTop: i === 0 ? "none" : "1px solid var(--hairline)" }}>
            <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center cursor-pointer hover:bg-[var(--surface)] transition-colors" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
              <div className="md:col-span-3">
                <p className="eyebrow mb-1" style={{ color: p.status === "new" ? "var(--brass)" : "var(--ink-faint)" }}>{p.status}</p>
                <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>
                  {FRANCHISE_TIER_LABEL[p.tier] || p.tier} · {PAYMENT_OPTION_LABEL[p.payment_option] || p.payment_option}
                </p>
              </div>
              <div className="md:col-span-4">
                <p className="font-display text-xl">{p.business_name}</p>
                <p className="text-xs" style={{ color: "var(--ink-muted)" }}>{p.owner_name} · {p.email}</p>
              </div>
              <div className="md:col-span-3 text-sm" style={{ color: "var(--brass)" }}>
                ${(p.total_due_today || 0).toLocaleString()} due today
              </div>
              <div className="md:col-span-2 text-xs text-right" style={{ color: "var(--ink-faint)" }}>
                {new Date(p.submitted_at).toLocaleDateString()}
              </div>
            </div>
            {expanded === p.id && (
              <div className="px-5 pb-5 pt-2 text-sm" style={{ background: "var(--surface)", borderTop: "1px solid var(--hairline)" }}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div><p className="eyebrow mb-1">Phone</p><p>{p.phone || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Website</p><p>{p.website ? <a href={p.website} target="_blank" rel="noreferrer" className="brass-link">{p.website}</a> : "—"}</p></div>
                  <div><p className="eyebrow mb-1">Territory</p><p>{p.territory || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Experience</p><p>{p.experience || "—"}</p></div>
                  <div><p className="eyebrow mb-1">Setup fee</p><p style={{ color: "var(--brass)" }}>${(p.setup_fee_due_today || 0).toLocaleString()}</p></div>
                  <div><p className="eyebrow mb-1">Monthly</p><p style={{ color: "var(--brass)" }}>${(p.monthly_fee || 0).toLocaleString()}</p></div>
                </div>
                {p.notes && (<><p className="eyebrow mb-2">Notes</p><p className="whitespace-pre-wrap mb-6" style={{ color: "var(--ink)" }}>{p.notes}</p></>)}
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="eyebrow">Status:</span>
                  {FRANCHISE_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatus(p.id, s)}
                      disabled={p.status === s}
                      data-testid={`franchise-status-${p.id}-${s}`}
                      className="text-xs uppercase tracking-[0.16em] px-3 py-1.5 transition-colors"
                      style={{
                        border: "1px solid",
                        borderColor: p.status === s ? "var(--brass)" : "var(--hairline-strong)",
                        color: p.status === s ? "var(--brass)" : "var(--ink)",
                        opacity: p.status === s ? 0.6 : 1,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                  <button onClick={() => remove(p.id)} className="ml-auto text-xs uppercase tracking-[0.16em] px-3 py-1.5" style={{ color: "#E08585", border: "1px solid rgba(224,133,133,0.4)" }} data-testid={`delete-franchise-${p.id}`}>
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


const RAIL_LABEL = {
  stripe: "Card · ACH · CashApp · Klarna",
  stripe_crypto: "Stripe Crypto (USDC)",
  web3_wallet: "Web3 / NFT Wallet",
};

const STATUS_COLOR = {
  paid: "var(--brass)",
  complete: "var(--brass)",
  pending: "#cc9966",
  pending_verification: "#cc9966",
  pending_configuration: "#cc9966",
  initiated: "var(--ink-muted)",
  failed: "#E08585",
  expired: "#E08585",
};

function PaymentsAdmin({ items }) {
  const total = items.length;
  const paid = items.filter((p) => p.payment_status === "paid").length;
  const pending = items.filter((p) => ["pending", "initiated", "pending_verification", "pending_configuration"].includes(p.payment_status || p.status)).length;
  const gross = items.filter((p) => p.payment_status === "paid").reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div data-testid="admin-payments-list">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Total transactions" value={total} />
        <Metric label="Paid" value={paid} highlight />
        <Metric label="Pending" value={pending} />
        <Metric label="Gross (paid USD)" value={`$${gross.toLocaleString()}`} highlight />
      </div>

      {items.length === 0 && (
        <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No payment transactions yet.</p>
      )}

      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="p-5" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }} data-testid={`payment-row-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: "var(--ink-muted)" }}>
                  {RAIL_LABEL[p.rail] || p.rail} · {p.provider}
                </p>
                <p className="font-display text-xl mb-1">
                  ${Number(p.amount || 0).toLocaleString()} · {p.metadata?.label || p.package_id}
                </p>
                <p className="text-xs" style={{ color: "var(--ink-faint)" }}>
                  {p.created_at?.slice(0, 19).replace("T", " ")} ·{" "}
                  {p.session_id ? `session ${p.session_id.slice(-12)}` : ""}
                  {p.wallet_address ? ` wallet ${p.wallet_address.slice(0, 12)}…` : ""}
                </p>
              </div>
              <span
                className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                style={{
                  border: "1px solid",
                  borderColor: STATUS_COLOR[p.payment_status] || STATUS_COLOR[p.status] || "var(--hairline)",
                  color: STATUS_COLOR[p.payment_status] || STATUS_COLOR[p.status] || "var(--ink)",
                }}
              >
                {p.payment_status || p.status}
              </span>
            </div>
            {(p.wallet_address || p.tx_hash || p.nft_token_id) && (
              <div className="mt-2 pt-3 border-t text-xs space-y-1" style={{ borderColor: "var(--hairline)", color: "var(--ink-muted)" }}>
                {p.wallet_address && <div><b>Wallet:</b> <code>{p.wallet_address}</code></div>}
                {p.chain && <div><b>Chain:</b> {p.chain} · <b>Asset:</b> {p.asset}</div>}
                {p.tx_hash && <div><b>Tx:</b> <code>{p.tx_hash}</code></div>}
                {p.nft_contract && <div><b>NFT:</b> {p.nft_contract} #{p.nft_token_id}</div>}
                {p.note && <div><b>Note:</b> {p.note}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className="p-5" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}>
      <p className="text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--ink-muted)" }}>{label}</p>
      <p className="font-display text-3xl" style={{ color: highlight ? "var(--brass)" : "var(--ink)" }}>{value}</p>
    </div>
  );
}


function CreditReadinessAdmin({ items }) {
  const total = items.length;
  const ready = items.filter((r) => r.report_status === "ready").length;
  const queued = items.filter((r) => ["queued", "generating", "draft"].includes(r.report_status)).length;
  const avgScore = (() => {
    const withScores = items.filter((r) => r.approval_score !== null && r.approval_score !== undefined);
    if (withScores.length === 0) return "—";
    return Math.round(withScores.reduce((s, r) => s + r.approval_score, 0) / withScores.length);
  })();

  return (
    <div data-testid="admin-credit-readiness-list">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Metric label="Total diagnostics" value={total} />
        <Metric label="Reports ready" value={ready} highlight />
        <Metric label="In progress" value={queued} />
        <Metric label="Avg approval score" value={avgScore} highlight />
      </div>

      {items.length === 0 && (
        <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No Credit Readiness diagnostics yet.</p>
      )}

      <div className="space-y-3">
        {items.map((r) => {
          const i = r.inputs || {};
          return (
            <div key={r.id} className="p-5" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }} data-testid={`cr-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: "var(--ink-muted)" }}>
                    {i.entity_type || "—"} · {i.state_of_formation || "—"} · {i.industry_description?.slice(0, 50) || "—"}
                  </p>
                  <p className="font-display text-xl mb-1">
                    {i.business_name || "—"} <span style={{ color: "var(--ink-muted)" }}>· {i.full_name || "—"}</span>
                  </p>
                  <p className="text-xs" style={{ color: "var(--ink-faint)" }}>
                    {i.email} · FICO {i.personal_fico_band} · target ${Number(i.target_funding_amount_usd || 0).toLocaleString()} · {r.created_at?.slice(0, 19).replace("T", " ")}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {r.approval_score !== null && r.approval_score !== undefined && (
                    <span
                      className="font-display text-2xl"
                      style={{ color: "var(--brass)" }}
                      data-testid={`cr-score-${r.id}`}
                    >
                      {r.approval_score}/100
                    </span>
                  )}
                  <span
                    className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                    style={{
                      border: "1px solid",
                      borderColor:
                        r.report_status === "ready" ? "var(--brass)" :
                          r.report_status === "error" ? "#E08585" : "var(--hairline-strong)",
                      color:
                        r.report_status === "ready" ? "var(--brass)" :
                          r.report_status === "error" ? "#E08585" : "var(--ink-muted)",
                    }}
                  >
                    {r.report_status}
                  </span>
                  {r.report_status === "ready" && (
                    <a
                      href={`/credit-readiness/report/${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs uppercase tracking-[0.18em] brass-link"
                      data-testid={`cr-open-${r.id}`}
                    >
                      Open report →
                    </a>
                  )}
                  {(r.report_status === "error" || r.report_status === "ready") && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post(`/credit-readiness/${r.id}/regenerate`);
                          window.location.reload();
                        } catch (err) {
                          alert("Regenerate failed: " + (err.response?.data?.detail || err.message));
                        }
                      }}
                      className="text-xs uppercase tracking-[0.15em]"
                      style={{
                        border: "1px solid var(--hairline-strong)",
                        color: "var(--ink-muted)",
                        padding: "4px 10px",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                      data-testid={`cr-regenerate-${r.id}`}
                    >
                      ↻ Regenerate
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function NewsletterAdmin({ issues, subscribers, stats, reload }) {
  const [showCreate, setShowCreate] = useState(false);
  const [theme, setTheme] = useState("");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return d.toLocaleString("en-US", { month: "long", year: "numeric" });
  });
  const [publishOnReady, setPublishOnReady] = useState(true);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!theme.trim()) return;
    setBusy(true);
    try {
      await api.post("/newsletter/issues", { theme, month_label: month, publish: publishOnReady });
      setTheme("");
      setShowCreate(false);
      reload();
    } catch (err) {
      alert("Create failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  const publish = async (id) => {
    try {
      await api.post(`/newsletter/issues/${id}/publish`);
      reload();
    } catch (err) {
      alert("Publish failed: " + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div data-testid="admin-newsletter-tab" className="space-y-8">
      {/* Subscriber stats */}
      <div>
        <p className="eyebrow mb-3">Subscribers</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric label="Total subscribers" value={stats.total || 0} />
          <Metric label="Active" value={stats.active || 0} highlight />
          <Metric label="Premium tier" value={(stats.by_tier && stats.by_tier.premium) || 0} highlight />
          <Metric label="Advisor tier" value={(stats.by_tier && stats.by_tier.advisor) || 0} />
        </div>
      </div>

      {/* Issues + Create */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <p className="eyebrow">Monthly Issues</p>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="text-xs uppercase tracking-[0.18em]"
            style={{ border: "1px solid var(--brass)", color: "var(--brass)", padding: "8px 14px", background: "transparent" }}
            data-testid="newsletter-admin-new-issue"
          >
            {showCreate ? "× Cancel" : "+ New Issue"}
          </button>
        </div>

        {showCreate && (
          <div className="p-5 mb-4" style={{ border: "1px solid var(--brass)", background: "var(--surface)" }} data-testid="newsletter-admin-create-panel">
            <label className="eyebrow block mb-2">Month Label</label>
            <input value={month} onChange={(e) => setMonth(e.target.value)} className="input-line mb-3" data-testid="newsletter-admin-month" />
            <label className="eyebrow block mb-2">This Month&apos;s Theme / Editor Thesis</label>
            <textarea
              rows={3}
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              placeholder="e.g. Concentration risk and the discipline of patient capital"
              className="w-full bg-transparent p-3 mb-3"
              style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }}
              data-testid="newsletter-admin-theme"
            />
            <label className="flex items-center gap-2 mb-4 text-sm" style={{ color: "var(--ink-muted)" }}>
              <input type="checkbox" checked={publishOnReady} onChange={(e) => setPublishOnReady(e.target.checked)} style={{ accentColor: "var(--brass)" }} data-testid="newsletter-admin-publish-toggle" />
              Publish immediately when AI generation completes
            </label>
            <button
              onClick={create}
              disabled={busy || !theme.trim()}
              className="btn-brass"
              data-testid="newsletter-admin-create-submit"
            >
              {busy ? "Queuing…" : "Run WealthFirst FundingBot"}
            </button>
          </div>
        )}

        <div className="space-y-3">
          {issues.length === 0 && (
            <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No issues yet. Click &quot;+ New Issue&quot; to generate one.</p>
          )}
          {issues.map((iss) => (
            <div key={iss.id} className="p-5 flex flex-wrap items-start justify-between gap-4" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }} data-testid={`newsletter-issue-row-${iss.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: "var(--ink-muted)" }}>{iss.month_label}</p>
                <p className="font-display text-xl mb-1">{iss.headline || iss.theme}</p>
                <p className="text-xs" style={{ color: "var(--ink-faint)" }}>{iss.slug} · {iss.created_at?.slice(0, 16).replace("T", " ")}</p>
                {iss.error && <p className="text-xs mt-2" style={{ color: "#E08585" }}>Error: {iss.error}</p>}
              </div>
              <div className="flex gap-2 items-center">
                <span
                  className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                  style={{
                    border: "1px solid",
                    borderColor:
                      iss.status === "published" ? "var(--brass)" :
                        iss.status === "error" ? "#E08585" : "var(--hairline-strong)",
                    color:
                      iss.status === "published" ? "var(--brass)" :
                        iss.status === "error" ? "#E08585" : "var(--ink-muted)",
                  }}
                >
                  {iss.status}
                </span>
                {iss.status === "published" && (
                  <a href={`/newsletter/${iss.slug}`} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-[0.18em] brass-link" data-testid={`newsletter-open-${iss.id}`}>View →</a>
                )}
                {iss.status === "ready" && (
                  <button onClick={() => publish(iss.id)} className="text-xs uppercase tracking-[0.18em]" style={{ border: "1px solid var(--brass)", color: "var(--brass)", padding: "6px 12px", background: "transparent" }} data-testid={`newsletter-publish-${iss.id}`}>
                    Publish
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Subscribers list */}
      <div>
        <p className="eyebrow mb-3">Subscriber List</p>
        <div className="space-y-1">
          {subscribers.slice(0, 50).map((s) => (
            <div key={s.id} className="p-3 flex items-center justify-between text-sm" style={{ borderBottom: "1px solid var(--hairline)" }} data-testid={`newsletter-sub-${s.id}`}>
              <div className="flex-1 min-w-0">
                <span style={{ color: "var(--ink)" }}>{s.email}</span>
                {s.name && <span style={{ color: "var(--ink-muted)" }}> · {s.name}</span>}
              </div>
              <div className="flex gap-3 text-xs uppercase tracking-[0.12em]">
                <span style={{ color: "var(--brass)" }}>{s.segment}</span>
                <span style={{ color: "var(--ink-muted)" }}>{s.tier}</span>
                <span style={{ color: s.status === "active" ? "var(--ink-muted)" : "#E08585" }}>{s.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



// ============ CAMPAIGNS ADMIN — ad-creative library + CTR ============
const CAMPAIGN_STATUS_COLOR = {
  active: "var(--brass)",
  draft: "var(--ink-muted)",
  archived: "#E08585",
};

function CampaignsAdmin({ items, reload }) {
  const [creating, setCreating] = useState(false);
  const total = items.length;
  const active = items.filter((c) => c.status === "active").length;
  const totalViews = items.reduce((s, c) => s + (c.views || 0), 0);
  const totalClicks = items.reduce((s, c) => s + (c.clicks || 0), 0);
  const totalLeads = items.reduce((s, c) => s + (c.leads_count || 0), 0);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/campaigns/${id}`, { status });
      toast.success(`Campaign ${status}.`);
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await api.delete(`/admin/campaigns/${id}`);
      toast.success("Deleted.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div data-testid="admin-campaigns-list">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Metric label="Campaigns" value={total} />
        <Metric label="Active" value={active} highlight />
        <Metric label="Total views" value={totalViews.toLocaleString()} />
        <Metric label="Total clicks" value={totalClicks.toLocaleString()} />
        <Metric label="Attributed leads" value={totalLeads} highlight />
      </div>

      <div className="flex justify-end mb-5">
        <button
          onClick={() => setCreating(true)}
          className="btn-brass"
          data-testid="campaign-create-button"
        >
          <Plus size={14} /> New Campaign
        </button>
      </div>

      {items.length === 0 && (
        <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No campaigns yet.</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {items.map((c) => {
          const ctr = (c.views || 0) > 0 ? ((c.clicks || 0) / c.views * 100).toFixed(1) : "0.0";
          return (
            <div
              key={c.id}
              className="p-5"
              style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }}
              data-testid={`campaign-card-${c.slug}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="eyebrow mb-1" style={{ color: "var(--brass)" }}>
                    Concept {c.concept} · {c.slug}
                  </p>
                  <h3 className="font-display text-2xl leading-tight">{c.name}</h3>
                </div>
                <span
                  className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                  style={{ border: "1px solid", borderColor: CAMPAIGN_STATUS_COLOR[c.status], color: CAMPAIGN_STATUS_COLOR[c.status] }}
                >
                  {c.status}
                </span>
              </div>

              {c.video_url ? (
                <video
                  controls
                  preload="metadata"
                  src={c.video_url}
                  poster={c.poster_url || undefined}
                  className="w-full mb-3"
                  style={{ maxHeight: 280, background: "var(--ink)" }}
                  data-testid={`campaign-video-${c.slug}`}
                />
              ) : (
                <div
                  className="w-full mb-3 flex items-center justify-center text-xs uppercase tracking-[0.18em] text-center px-4"
                  style={{ height: 200, border: "1px dashed var(--hairline-strong)", color: "var(--ink-muted)" }}
                >
                  Render pending — concept queued. Top up the Universal Key, then re-run the Sora 2 render to attach a video URL.
                </div>
              )}

              <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--ink-muted)" }}>
                {c.description}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-xs">
                <CampaignStat label="Views" value={c.views || 0} />
                <CampaignStat label="Clicks" value={c.clicks || 0} />
                <CampaignStat label="CTR" value={`${ctr}%`} />
                <CampaignStat label="Leads" value={c.leads_count || 0} highlight />
              </div>

              <p className="text-[11px] mb-3" style={{ color: "var(--ink-faint)" }}>
                <b>UTM:</b> {c.utm_campaign || "—"} · <b>CTA:</b> <code>{c.cta_url}</code>
              </p>

              <div className="flex gap-2 flex-wrap">
                {c.status !== "active" && (
                  <button
                    onClick={() => setStatus(c.id, "active")}
                    className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                    style={{ border: "1px solid var(--brass)", color: "var(--brass)" }}
                    data-testid={`campaign-activate-${c.slug}`}
                  >
                    Activate
                  </button>
                )}
                {c.status !== "draft" && (
                  <button
                    onClick={() => setStatus(c.id, "draft")}
                    className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                    style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink-muted)" }}
                    data-testid={`campaign-draft-${c.slug}`}
                  >
                    Move to Draft
                  </button>
                )}
                {c.status !== "archived" && (
                  <button
                    onClick={() => setStatus(c.id, "archived")}
                    className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                    style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink-muted)" }}
                    data-testid={`campaign-archive-${c.slug}`}
                  >
                    Archive
                  </button>
                )}
                <button
                  onClick={() => remove(c.id)}
                  className="text-xs uppercase tracking-[0.18em] px-3 py-1.5 ml-auto"
                  style={{ border: "1px solid #E08585", color: "#E08585" }}
                  data-testid={`campaign-delete-${c.slug}`}
                >
                  <Trash2 size={12} className="inline" /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {creating && (
        <CampaignCreateDialog
          onClose={() => setCreating(false)}
          onCreated={async () => { setCreating(false); await reload(); }}
        />
      )}
    </div>
  );
}

function CampaignStat({ label, value, highlight }) {
  return (
    <div className="px-3 py-2" style={{ border: "1px solid var(--hairline)" }}>
      <p className="eyebrow text-[10px] mb-1">{label}</p>
      <p className="font-display text-lg" style={{ color: highlight ? "var(--brass)" : "var(--ink)" }}>{value}</p>
    </div>
  );
}

function CampaignCreateDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", slug: "", concept: "custom", description: "",
    video_url: "", poster_url: "", utm_campaign: "", cta_url: "/lead", status: "draft",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) {
      toast.error("Name and slug are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/campaigns", form);
      toast.success("Campaign created.");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
      data-testid="campaign-create-dialog"
    >
      <div className="w-full max-w-xl p-8 max-h-[90vh] overflow-y-auto" style={{ background: "var(--surface)", border: "1px solid var(--brass)" }}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="eyebrow mb-2">New Campaign</p>
            <h2 className="font-display text-3xl leading-tight">Add ad creative.</h2>
          </div>
          <button onClick={onClose} data-testid="campaign-create-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Name *</label>
              <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="input-line" data-testid="campaign-name" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Slug *</label>
              <input required value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="vault-concept-x" className="input-line" data-testid="campaign-slug" />
            </div>
          </div>
          <div>
            <label className="eyebrow block mb-2">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full bg-transparent p-3 text-sm outline-none" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink)" }} data-testid="campaign-description" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Concept</label>
              <input value={form.concept} onChange={(e) => set("concept", e.target.value)} placeholder="A | B | C | custom" className="input-line" data-testid="campaign-concept" />
            </div>
            <div>
              <label className="eyebrow block mb-2">UTM Campaign (links to Leads)</label>
              <input value={form.utm_campaign} onChange={(e) => set("utm_campaign", e.target.value)} placeholder="vault_concept_x" className="input-line" data-testid="campaign-utm" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">Video URL</label>
              <input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="/campaigns/concept-x.mp4" className="input-line" data-testid="campaign-video-url" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Poster URL</label>
              <input value={form.poster_url} onChange={(e) => set("poster_url", e.target.value)} placeholder="/campaigns/concept-x.jpg" className="input-line" data-testid="campaign-poster-url" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="eyebrow block mb-2">CTA URL</label>
              <input value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} className="input-line" data-testid="campaign-cta-url" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className="input-line" style={{ background: "var(--ink)", color: "var(--ink-light)" }} data-testid="campaign-status">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 text-sm uppercase tracking-[0.18em]" style={{ border: "1px solid var(--hairline-strong)", color: "var(--ink-muted)" }} data-testid="campaign-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-brass flex-1" style={{ justifyContent: "center" }} data-testid="campaign-submit">
              {busy ? "Creating…" : "Create Campaign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ============ LEADS ADMIN — list + group-by-campaign UTM dashboard ============
const LEAD_STATUSES = ["new", "qualified", "contacted", "won", "archived"];
const LEAD_STATUS_COLOR = {
  new: "var(--ink-muted)",
  qualified: "var(--brass)",
  contacted: "#cc9966",
  won: "#9ACD32",
  archived: "#E08585",
};

function LeadsAdmin({ leads, byCampaign, reload }) {
  const [view, setView] = useState("all"); // "all" | "by-campaign"
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = statusFilter === "all" ? leads : leads.filter((l) => l.status === statusFilter);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success("Status updated.");
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed.");
    }
  };

  return (
    <div data-testid="admin-leads-list">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Metric label="Total leads" value={byCampaign?.totals?.total_leads ?? leads.length} />
        <Metric label="Qualified" value={byCampaign?.totals?.total_qualified ?? 0} highlight />
        <Metric label="Won" value={byCampaign?.totals?.total_won ?? 0} highlight />
        <Metric label="Qualified rate" value={`${byCampaign?.totals?.overall_qualified_rate ?? 0}%`} />
        <Metric label="Campaigns" value={byCampaign?.totals?.campaigns ?? 0} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-2">
          <button
            onClick={() => setView("all")}
            data-testid="leads-view-all"
            className="text-xs uppercase tracking-[0.18em] px-4 py-2"
            style={{ border: "1px solid", borderColor: view === "all" ? "var(--brass)" : "var(--hairline)", color: view === "all" ? "var(--brass)" : "var(--ink-muted)" }}
          >
            All Leads
          </button>
          <button
            onClick={() => setView("by-campaign")}
            data-testid="leads-view-by-campaign"
            className="text-xs uppercase tracking-[0.18em] px-4 py-2"
            style={{ border: "1px solid", borderColor: view === "by-campaign" ? "var(--brass)" : "var(--hairline)", color: view === "by-campaign" ? "var(--brass)" : "var(--ink-muted)" }}
          >
            By Campaign (UTM)
          </button>
        </div>

        {view === "all" && (
          <div className="flex gap-2 flex-wrap">
            {["all", ...LEAD_STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`leads-filter-${s}`}
                className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5"
                style={{ border: "1px solid", borderColor: statusFilter === s ? "var(--brass)" : "var(--hairline)", color: statusFilter === s ? "var(--brass)" : "var(--ink-muted)" }}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === "by-campaign" && (
        <div data-testid="leads-by-campaign-table">
          {(byCampaign?.rows || []).length === 0 ? (
            <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No UTM-attributed leads yet.</p>
          ) : (
            <div style={{ border: "1px solid var(--hairline)" }}>
              <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[11px] uppercase tracking-[0.18em]" style={{ borderBottom: "1px solid var(--hairline)", color: "var(--ink-muted)" }}>
                <div className="col-span-4">Campaign</div>
                <div className="col-span-1 text-right">Leads</div>
                <div className="col-span-1 text-right">Qualified</div>
                <div className="col-span-2 text-right">Qualified rate</div>
                <div className="col-span-2">Sources</div>
                <div className="col-span-2">Mediums</div>
              </div>
              {byCampaign.rows.map((r) => (
                <div key={r.campaign} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-center" style={{ borderBottom: "1px solid var(--hairline)" }} data-testid={`leads-by-campaign-row-${r.campaign}`}>
                  <div className="col-span-4 truncate" style={{ color: r.campaign === "(no campaign)" ? "var(--ink-muted)" : "var(--brass)" }}>{r.campaign}</div>
                  <div className="col-span-1 text-right font-display">{r.leads}</div>
                  <div className="col-span-1 text-right font-display">{r.qualified}</div>
                  <div className="col-span-2 text-right font-display" style={{ color: r.qualified_rate >= 25 ? "var(--brass)" : "var(--ink)" }}>{r.qualified_rate}%</div>
                  <div className="col-span-2 text-xs truncate" style={{ color: "var(--ink-muted)" }}>{r.sources.join(", ") || "—"}</div>
                  <div className="col-span-2 text-xs truncate" style={{ color: "var(--ink-muted)" }}>{r.mediums.join(", ") || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "all" && (
        <div className="space-y-3">
          {filtered.length === 0 && (
            <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No leads match this filter.</p>
          )}
          {filtered.map((l) => (
            <div key={l.id} className="p-5" style={{ border: "1px solid var(--hairline)", background: "var(--surface)" }} data-testid={`lead-row-${l.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="font-display text-xl mb-1">{l.name} <span style={{ color: "var(--ink-muted)" }}>· {l.email}</span></p>
                  {l.company && <p className="text-sm mb-1" style={{ color: "var(--ink-muted)" }}>{l.company}</p>}
                  <p className="text-sm leading-relaxed">{l.message}</p>
                </div>
                <select
                  value={l.status}
                  onChange={(e) => setStatus(l.id, e.target.value)}
                  className="text-xs uppercase tracking-[0.18em] px-3 py-1.5"
                  style={{ background: "var(--ink)", color: LEAD_STATUS_COLOR[l.status] || "var(--ink-muted)", border: "1px solid", borderColor: LEAD_STATUS_COLOR[l.status] || "var(--hairline)" }}
                  data-testid={`lead-status-${l.id}`}
                >
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-x-4 gap-y-1 text-[11px]" style={{ borderColor: "var(--hairline)", color: "var(--ink-faint)" }}>
                <span>{l.created_at?.slice(0, 19).replace("T", " ")}</span>
                {l.source && <span><b>Source:</b> {l.source}</span>}
                {l.utm_campaign && <span style={{ color: "var(--brass)" }}><b>Campaign:</b> {l.utm_campaign}</span>}
                {l.utm_source && <span><b>utm_source:</b> {l.utm_source}</span>}
                {l.utm_medium && <span><b>utm_medium:</b> {l.utm_medium}</span>}
                {l.utm_term && <span><b>utm_term:</b> {l.utm_term}</span>}
                {l.utm_content && <span><b>utm_content:</b> {l.utm_content}</span>}
                {l.referrer && <span><b>Referrer:</b> {l.referrer}</span>}
                {l.landing_path && <span><b>Landing:</b> {l.landing_path}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ LICENSES ADMIN — Brand-license recurring licensees ============
const LICENSE_STATUS_COLOR = {
  active: "var(--brass)",
  grace: "#cc9966",
  expired: "#E08585",
  revoked: "#E08585",
};

function LicensesAdmin({ data, reload }) {
  const [filter, setFilter] = useState("all");
  const totals = data?.totals || {};
  const list = data?.licensees || [];
  const filtered = filter === "all" ? list : list.filter((l) => l.computed_status === filter);

  return (
    <div data-testid="admin-licenses-list">
      {/* Headline metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Metric label="Active licensees" value={totals.active ?? 0} highlight />
        <Metric label="In grace (≤30d)" value={totals.grace ?? 0} />
        <Metric label="Expired" value={totals.expired ?? 0} />
        <Metric label="Annual Recurring Revenue" value={`$${(totals.arr_usd ?? 0).toLocaleString()}`} highlight />
        <Metric label="Implied MRR" value={`$${(totals.mrr_usd ?? 0).toLocaleString()}`} />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {["all", "active", "grace", "expired", "revoked"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className="text-xs uppercase tracking-[0.18em] px-4 py-2"
            style={{
              border: "1px solid",
              borderColor: filter === s ? "var(--brass)" : "var(--hairline)",
              color: filter === s ? "var(--brass)" : "var(--ink-muted)",
            }}
            data-testid={`licenses-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <p className="p-10 text-center" style={{ color: "var(--ink-muted)" }}>No licensees match this filter.</p>
      ) : (
        <div style={{ border: "1px solid var(--hairline)" }}>
          <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[11px] uppercase tracking-[0.18em]" style={{ borderBottom: "1px solid var(--hairline)", color: "var(--ink-muted)" }}>
            <div className="col-span-4">Licensee</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Expires</div>
            <div className="col-span-2">Purchased</div>
            <div className="col-span-1 text-right">Paid</div>
            <div className="col-span-1 text-right">Days</div>
          </div>
          {filtered.map((l) => {
            const daysLeft = l.expires_at
              ? Math.ceil((new Date(l.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
              : null;
            const statusColor = LICENSE_STATUS_COLOR[l.computed_status] || "var(--ink-muted)";
            return (
              <div
                key={l.id}
                className="grid grid-cols-12 gap-3 px-4 py-3 text-sm items-center"
                style={{ borderBottom: "1px solid var(--hairline)" }}
                data-testid={`license-row-${l.id}`}
              >
                <div className="col-span-4 min-w-0">
                  <p className="font-display text-lg leading-tight truncate">{l.name || l.email}</p>
                  <p className="text-xs truncate" style={{ color: "var(--ink-muted)" }}>{l.email}</p>
                  {l.company && <p className="text-[11px] mt-1" style={{ color: "var(--ink-faint)" }}>{l.company}</p>}
                </div>
                <div className="col-span-2">
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] px-2 py-1"
                    style={{ border: "1px solid", borderColor: statusColor, color: statusColor }}
                    data-testid={`license-status-${l.id}`}
                  >
                    {l.computed_status}
                  </span>
                </div>
                <div className="col-span-2 text-xs" style={{ color: "var(--ink-muted)" }}>
                  {l.expires_at?.slice(0, 10) || "—"}
                </div>
                <div className="col-span-2 text-xs" style={{ color: "var(--ink-muted)" }}>
                  {l.purchased_at?.slice(0, 10) || "—"}
                </div>
                <div className="col-span-1 text-right font-display">${(l.amount_paid || 0).toLocaleString()}</div>
                <div className="col-span-1 text-right text-xs" style={{ color: daysLeft !== null && daysLeft < 30 ? "#E08585" : "var(--ink-muted)" }}>
                  {daysLeft !== null ? (daysLeft > 0 ? `${daysLeft}d` : "expired") : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs mt-6" style={{ color: "var(--ink-faint)" }}>
        Licensees are automatically created when a <code>brand_license_annual</code> Stripe checkout completes.
        Renewals extend <code>expires_at</code> by 365 days from the later of (today, current expiry).
        Email reminders fire 30/14/7 days before expiry once the renewal cron is wired.
      </p>
    </div>
  );
}

